import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Shield, Key, Lock, Globe, Users, Workflow, School, GraduationCap } from 'lucide-react';
import Button from '../common/Button';

const LemonLDAPSection = () => {
  const { t } = useTranslatedContent('lemonldap');

  return (
    <div id="lemonldap" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-900 via-orange-900 to-red-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-amber-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-orange-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-red-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500/10 border border-amber-400/20 backdrop-blur-sm mb-8">
            <Shield className="h-5 w-5 text-amber-400 mr-2" />
            <span className="text-amber-200 font-medium">Enterprise SSO Solution</span>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-amber-100 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Core Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {[
            {
              icon: Shield,
              title: 'Web SSO Solution',
              description: 'Complete Single Sign-On solution with multi-protocol support'
            },
            {
              icon: Key,
              title: 'Identity Federation',
              description: 'SAML 2.0 and OpenID Connect federation capabilities'
            },
            {
              icon: Lock,
              title: 'Access Management',
              description: 'Fine-grained access control and policy management'
            },
            {
              icon: Globe,
              title: 'Global Integration',
              description: 'Seamless integration with international identity federations'
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-amber-500 to-red-500 rounded-lg">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-amber-100">{feature.description}</p>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-amber-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-red-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>

        {/* Education Federation Features */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-white mb-4">Education Federation Integration</h3>
            <p className="text-amber-100">Seamless integration with international education identity federations</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* EduGAIN Integration */}
            <div className="relative group">
              <div className="absolute inset-0.5 bg-gradient-to-r from-amber-500 to-red-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
              <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <School className="h-8 w-8 text-amber-400 mr-3" />
                  <h4 className="text-xl font-semibold text-white">EduGAIN Integration</h4>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>Access to global academic resources across 70+ countries</span>
                  </li>
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>Automated metadata exchange and updates</span>
                  </li>
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>Standards-compliant SAML2 implementation</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Erasmus+ Integration */}
            <div className="relative group">
              <div className="absolute inset-0.5 bg-gradient-to-r from-amber-500 to-red-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
              <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <GraduationCap className="h-8 w-8 text-amber-400 mr-3" />
                  <h4 className="text-xl font-semibold text-white">Erasmus+ Support</h4>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>European Student Card Initiative integration</span>
                  </li>
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>Erasmus Without Paper Network support</span>
                  </li>
                  <li className="flex items-start text-amber-100">
                    <div className="p-1 bg-amber-500/20 rounded-full mr-3 mt-1">
                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                    </div>
                    <span>Seamless student mobility services</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Process */}
        <div className="relative mb-20">
          <h3 className="text-2xl font-semibold text-white text-center mb-12">Integration Process</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: 'Identity Analysis',
                description: 'Assessment of current identity infrastructure and requirements'
              },
              {
                icon: Shield,
                title: 'Federation Setup',
                description: 'Configuration of federation protocols and metadata exchange'
              },
              {
                icon: Workflow,
                title: 'Service Integration',
                description: 'Integration with academic services and resources'
              },
              {
                icon: Lock,
                title: 'Security Validation',
                description: 'Testing and security compliance verification'
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative group">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-500 to-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>

                  {/* Card */}
                  <div className="pt-8 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-colors duration-300">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-gradient-to-br from-amber-500 to-red-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      
                      <h4 className="text-lg font-semibold text-white mb-2">
                        {step.title}
                      </h4>
                      
                      <p className="text-amber-100">
                        {step.description}
                      </p>

                      {/* Arrow indicator */}
                      {index < 3 && (
                        <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-400 group-hover:scale-125 transition-transform duration-300">
                          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            variant="secondary"
            className="bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600 text-white border-0"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('hero.buttonText')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LemonLDAPSection;